import {useContext, useState, useEffect, useMemo, useCallback} from "react";
import {IAM_SERVER_SERVICE_AUTH, IAM_SERVER_SERVICE_PAGE, IAM_SERVER_SERVICE_PAGEAUTH} from "../../../utils/Config";
import axios from "../../../utils/api/axios";
import styles from './PageAuthRegisterModal.module.css';
import { InnocodeButton, InnocodeDatatable, useDialog } from '@intsoft/innocode-core';


/**
 * 페이지 권한 등록 모달 Container
 * @param update
 * @returns {JSX.Element}
 * @constructor
 */
export const PageAuthRegisterModal = ({update, selectedServiceId, pageId, onClose}) => {
    const { showDialog } = useDialog();

    const [serviceAuthList, setServiceAuthList] = useState([]);
    const [pageAuths, setPageAuths] = useState([]);
    const [availableAuths, setAvailableAuths] = useState([]);
    const [assignedAuths, setAssignedAuths] = useState([]);
    const [leftCheckedAuths, setLeftCheckedAuths] = useState([]);
    const [rightCheckedAuths, setRightCheckedAuths] = useState([]);
    const [isLeftSelectAll, setIsLeftSelectAll] = useState(false);
    const [isRightSelectAll, setIsRightSelectAll] = useState(false);
    const [tableResetKey, setTableResetKey] = useState(0);


    useEffect(() => {
        // 서비스 권한 리스트 가져오기
        axios.get(`${IAM_SERVER_SERVICE_AUTH}/get/service/${selectedServiceId}`)
        .then(serviceRes => {
            // 페이지 권한 리스트 가져오기
            axios.get(`${IAM_SERVER_SERVICE_PAGEAUTH}/get/authAll/${pageId}`)
            .then(pageRes => {

                const pageAuths = pageRes.data.data;
                const serviceAuths = serviceRes.data.data;

                setServiceAuthList(serviceAuths);
                setPageAuths(pageAuths);
                setAssignedAuths(pageAuths);
                const assignedIds = pageAuths.map(a => a.authorityId);
                const filtered = serviceAuths.filter(a => !assignedIds.includes(a.authorityId));
                setAvailableAuths(filtered);
            })
            .catch(err => {
                console.error("페이지 권한 목록 불러오기 실패", err);
            });
        })
        .catch(err => {
            console.error("서비스 권한 목록 불러오기 실패", err);
        });


    }, [selectedServiceId, pageId]);

    // 전체선택 핸들러 (좌측)
    useEffect(() => {
        if (isLeftSelectAll) {
            setLeftCheckedAuths(availableAuths);
        } else {
            setLeftCheckedAuths([]);
        }
    }, [isLeftSelectAll, availableAuths]);

    // 전체선택 핸들러 (우측)
    useEffect(() => {
        if (isRightSelectAll) {
            setRightCheckedAuths(assignedAuths);
        } else {
            setRightCheckedAuths([]);
        }
    }, [isRightSelectAll, assignedAuths]);

    // 왼쪽 → 오른쪽 (권한 추가)
    const handleAssign = useCallback(() => {
        if (leftCheckedAuths.length === 0) return;
        
        setAssignedAuths(prev => [...prev, ...leftCheckedAuths]);
        setAvailableAuths(prev => prev.filter(auth => !leftCheckedAuths.some(a => a.authorityId === auth.authorityId)));
        setLeftCheckedAuths([]);
        setIsLeftSelectAll(false);
        setTableResetKey(prev => prev + 1);
    }, [leftCheckedAuths]);

    // 오른쪽 → 왼쪽 (권한 제거)
    const handleUnassign = useCallback(() => {
        if (rightCheckedAuths.length === 0) return;
        
        setAvailableAuths(prev => [...prev, ...rightCheckedAuths]);
        setAssignedAuths(prev => prev.filter(auth => !rightCheckedAuths.some(a => a.authorityId === auth.authorityId)));
        setRightCheckedAuths([]);
        setIsRightSelectAll(false);
        setTableResetKey(prev => prev + 1);
    }, [rightCheckedAuths]);

    // 저장 로직
    const handleSave = useCallback(() => {
        const currentIds = assignedAuths.map(a => a.authorityId);
        const originalIds = pageAuths.map(a => a.authorityId);

        const added = currentIds.filter(id => !originalIds.includes(id));
        const removedAuthorityIds = originalIds.filter(id => !currentIds.includes(id));

        // 아무 변경 없다면 그냥 패스하고 종료
        if (added.length === 0 && removedAuthorityIds.length === 0) {
            onClose();
            return;
        }

        const promises = [];
        
        // 제거된 권한 삭제로직
        if(removedAuthorityIds.length > 0){
            const removedDetailIds = pageAuths
                .filter(auth => removedAuthorityIds.includes(auth.authorityId))
                .map(auth => auth.authorityDetailId);

            const params = new URLSearchParams();
            removedDetailIds.forEach(id => params.append("removedAuthorityIds", id));
            promises.push(
                axios.post(
                    `${IAM_SERVER_SERVICE_PAGEAUTH}/delete/list?${params.toString()}`
                )
            );
        }

        // 추가된 권한 추가로직
        if(added.length > 0){
            for (const id of added) {
                const data = {
                    accessPageId: pageId,
                    accessAuthorityId: id,
                    create: false,
                    read: false,
                    update: false,
                    delete: false
                };
                promises.push(
                    axios.post(`${IAM_SERVER_SERVICE_PAGEAUTH}/create`, data)
                );
            }
        }

        Promise.all(promises)
            .then(() => {
                showDialog({
                    iconType: "check",
                    type: 'info',
                    title: "성공",
                    message: "저장되었습니다.",
                    confirmText: "확인",
                    onConfirm: () => {
                        update(pageId);
                        onClose();
                    }
                });
            })
            .catch(err => {
                showDialog({
                    iconType: "warning",
                    type: 'error',
                    title: "실패",
                    message: err.response?.data?.message || "저장 실패",
                    confirmText: "확인",
                });
            });
    }, [assignedAuths, pageAuths, pageId, update, onClose, showDialog]);


    // 좌측 테이블 체크박스 변경
    const handleLeftRowSelectionChange = useCallback((row, index, isChecked) => {
        if (isChecked) {
            setLeftCheckedAuths(prev => [...prev, row]);
        } else {
            setLeftCheckedAuths(prev => prev.filter(a => a.authorityId !== row.authorityId));
        }
    }, []);

    // 우측 테이블 체크박스 변경
    const handleRightRowSelectionChange = useCallback((row, index, isChecked) => {
        if (isChecked) {
            setRightCheckedAuths(prev => [...prev, row]);
        } else {
            setRightCheckedAuths(prev => prev.filter(a => a.authorityId !== row.authorityId));
        }
    }, []);

    // 미선택 테이블 컬럼 정의
    const leftColumns = useMemo(() => [
        {
            id: 'authorityName',
            header: '권한 명',
            accessorKey: 'authorityName',
            enableSorting: false,
            cell: ({ row }) => (
                <span className={styles.cellText}>{row.authorityName}</span>
            ),
        },
        {
            id: 'authorityEngName',
            header: '영문명',
            accessorKey: 'authorityEngName',
            enableSorting: false,
            cell: ({ row }) => (
                <span className={styles.cellText}>{row.authorityEngName}</span>
            ),
        },
    ], []);

    // 선택됨 테이블 컬럼 정의
    const rightColumns = useMemo(() => [
        {
            id: 'authorityName',
            header: '권한 명',
            accessorKey: 'authorityName',
            enableSorting: false,
            cell: ({ row }) => (
                <span className={styles.cellText}>{row.authorityName}</span>
            ),
        },
        {
            id: 'authorityEngName',
            header: '영문명',
            accessorKey: 'authorityEngName',
            enableSorting: false,
            cell: ({ row }) => {
                const matched = serviceAuthList.find(a => String(a.authorityId) === String(row.authorityId));
                const engName = row.authorityEngName || matched?.authorityEngName || '';
                return <span className={styles.cellText}>{engName}</span>;
            },
        },
    ], [serviceAuthList]);

    return (
        <div className={styles.layoutContainer}>
            <div className={styles.scrollContentArea}>
                <div className={styles.minHeightArea}>
                    <div className={styles.contentAreaWrapper}>
                        <div className={styles.contentAreaContent}>
                            <div className={styles.contentAreaTitle}>
                                <h1 className={`innocode-typography--headline-md innocode-typography--bold ${styles.contentAreaTitleText}`}>
                                    권한 등록
                                </h1>
                                <InnocodeButton
                                    theme="opacity"
                                    designType='assistive'
                                    enablePadding={false}
                                    iconOnly={true}
                                    icon={
                                        <svg className="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                                            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18 17.94 6M18 18 6.06 6" />
                                        </svg>
                                    }
                                    onClick={onClose}
                                />
                            </div>
                            <div className={styles.contentAreaList}>
                                <div className={styles.authSelectionContainer}>
                                    {/* 좌측 - 미선택 권한 리스트 */}
                                    <div className={styles.tableSection}>
                                        <div className={styles.tableSectionHeader}>
                                            <span className={styles.tableSectionTitle}>미선택</span>
                                        </div>
                                        <div className={styles.tableWrapper}>
                                            <InnocodeDatatable
                                                key={`left-${tableResetKey}-${isLeftSelectAll}`}
                                                className={styles.datatable}
                                                data={availableAuths}
                                                columns={leftColumns}
                                                enableCustomHeader={true}
                                                headerCheckboxSelectsAll={isLeftSelectAll}
                                                manualPagination={false}
                                                enableSorting={false}
                                                enableCheckbox={true}
                                                enableInfiniteScroll={true}
                                                isLoading={false}
                                                emptyMessage="권한이 없습니다."
                                                onRowSelectionChange={handleLeftRowSelectionChange}
                                                onSelectAllChange={(isSelected) => setIsLeftSelectAll(isSelected)}
                                            />
                                        </div>
                                    </div>

                                    {/* 중앙 - 이동 버튼 */}
                                    <div className={styles.centerButtons}>
                                        <InnocodeButton
                                            size="sm"
                                            designType="primary"
                                            theme="line"
                                            onClick={handleAssign}
                                            disabled={leftCheckedAuths.length === 0}
                                            iconOnly={true}
                                            icon={
                                                <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 12H5m14 0-4 4m4-4-4-4"/>
                                                </svg>
                                            }
                                        />
                                        <InnocodeButton
                                            size="sm"
                                            designType="assistive"
                                            theme="line"
                                            onClick={handleUnassign}
                                            disabled={rightCheckedAuths.length === 0}
                                            iconOnly={true}
                                            icon={
                                                <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14M5 12l4-4m-4 4 4 4"/>
                                                </svg>
                                            }
                                        />
                                    </div>

                                    {/* 우측 - 선택된 권한 리스트 */}
                                    <div className={styles.tableSection}>
                                        <div className={styles.tableSectionHeader}>
                                            <span className={styles.tableSectionTitle}>선택됨</span>
                                        </div>
                                        <div className={styles.tableWrapper}>
                                            <InnocodeDatatable
                                                className={styles.datatable}
                                                key={`right-${tableResetKey}-${isRightSelectAll}`}
                                                data={assignedAuths}
                                                columns={rightColumns}
                                                enableCustomHeader={true}
                                                headerCheckboxSelectsAll={isRightSelectAll}
                                                manualPagination={false}
                                                enableSorting={false}
                                                enableCheckbox={true}
                                                enableInfiniteScroll={true}
                                                isLoading={false}
                                                emptyMessage="권한이 없습니다."
                                                onRowSelectionChange={handleRightRowSelectionChange}
                                                onSelectAllChange={(isSelected) => setIsRightSelectAll(isSelected)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.actionButtonWrapper}>
                                    <InnocodeButton
                                        className={styles.actionButton}
                                        theme="opacity"
                                        designType="primary"
                                        size="lg"
                                        onClick={onClose}
                                    >
                                        취소
                                    </InnocodeButton>
                                    <InnocodeButton
                                        className={styles.actionButton}
                                        theme="solid"
                                        size="lg"
                                        onClick={handleSave}
                                    >
                                        저장
                                    </InnocodeButton>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}